import React from "react";
import Box from "@mui/joy/Box";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import { AnalyticsFilters, AnalyticsFiltersState } from "../../../bids/analytics/components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../../bids/analytics/components/AnalyticsShared";
import { AnalyticsSeriesChart } from "../../../bids/analytics/components/AnalyticsSeriesChart";
import { useAsyncBlock } from "../../../bids/analytics/hooks";
import { formatInt, formatMoneyKopecks, toNum } from "../../../bids/analytics/format";
import { ChartGranularity } from "../../../../models/bidAnalytics";
import { Game } from "../../../../models/games";
import { OrderSummaryReport, OrderSummaryRow } from "../../../../models/orderAnalytics";
import { ordersSummaryApi } from "../ordersAnalyticsApi";
import { GROUPS, groupCards, toGameBars, toStatsGranularity, toSummaryDataset } from "../summary";
import { toggleKey, visibleSeries } from "../series";
import { HowTo, SeriesToggle } from "../Shared";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

const ORDER_SERIES = [
    { dataKey: "totalCount", label: "Всего", color: "#455a64" },
    { dataKey: "successCount", label: GROUPS[0].label, color: GROUPS[0].color },
    { dataKey: "failedCount", label: GROUPS[1].label, color: GROUPS[1].color },
];

function formatPercent(value: number | null | undefined): string {
    return value == null ? "—" : `${value.toLocaleString("ru-RU", { maximumFractionDigits: 1 })}%`;
}

export function SummaryTab({ isActive, filters, onFiltersChange, games, gamesLoading }: Props) {
    const [state, refresh] = useAsyncBlock<OrderSummaryReport>(
        () =>
            ordersSummaryApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
                granularity: toStatsGranularity(filters.granularity),
            }),
        isActive,
        [filters.from, filters.to, filters.gameId, filters.granularity]
    );
    const report = state.data;
    const isEmpty = report != null && toNum(report.total.totalCount) === 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showGranularity
            />
            {state.loading && !report && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {isEmpty && <EmptyState />}
            {report && !isEmpty && <SummaryContent report={report} granularity={filters.granularity} />}
        </Box>
    );
}

const HOW_TO_READ = (
    <HowTo items={[
        ["Штуки и суммы", "предметы ордеров, созданных в выбранном периоде, и их цена в ₽; один ордер может содержать несколько предметов"],
        ["Успешные", "ордера в статусе «Завершен» (Completed)"],
        ["Неуспешные", "«Ошибка» (Failed) и «Возврат» (Refunded); число возвратов показано отдельно"],
        ["В работе", "ещё исполняются: New, Pending, ожидание продавца, предметы получены, ожидание покупателя, а также ручной режим без причины — новый заказ продавца, который ждёт ручного запуска обработки"],
        ["Требуют внимания", "ручной режим с причиной (ManualMode) и отмена (Cancelling) — нужна реакция администратора"],
        ["% от всего", "доля группы среди всех предметов периода; доли четырёх групп в сумме дают ровно 100"],
        ["Success rate", "успешные / (успешные + неуспешные) — без тех, что ещё в работе или требуют внимания"],
    ]} />
);

const WATCH_FOR = (
    <WatchSignals items={[
        {
            signal: "Success rate",
            ifGrows: "больше начатых сделок доходит до покупателя.",
            ifFalls: "растёт доля неуспешных — откройте «Неуспешные» и посмотрите, какие причины выросли.",
        },
        {
            signal: "«Требуют внимания»",
            growsIsGood: false,
            ifGrows: "копятся ордера в ручном режиме и отмене — их нужно разобрать в списке ордеров.",
            ifFalls: "администраторы успевают разбирать ручные случаи.",
        },
        {
            signal: "«В работе» за старый период",
            growsIsGood: false,
            ifGrows: "ордера зависают — смотрите блок «Зависшие» на вкладке «Скорость».",
            ifFalls: "ордера периода доходят до финала.",
        },
    ]} />
);

function SummaryContent({ report, granularity }: { report: OrderSummaryReport; granularity: ChartGranularity }) {
    const cards = groupCards(report.total);
    const bars = toGameBars(report.byGame);
    const dataset = toSummaryDataset(report.series);
    const [hiddenGroups, setHiddenGroups] = React.useState<ReadonlySet<string>>(new Set());
    const [hiddenOrders, setHiddenOrders] = React.useState<ReadonlySet<string>>(new Set());
    const groupSeries = bars.series.map((s) => ({ ...s, dataKey: s.key }));

    return (
        <>
            <AnalyticsCard title="Итог за период" subtitle="Ордера по группам статусов" howToRead={HOW_TO_READ}
                           watchFor={WATCH_FOR} snapshotLabel="period">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <MetricHint label="Всего" value={formatInt(report.total.totalCount)}
                                hint={formatMoneyKopecks(report.total.totalSum)} />
                    {cards.map((card) => (
                        <MetricHint
                            key={card.key}
                            label={card.label}
                            color={card.jColor}
                            value={formatInt(card.count)}
                            hint={
                                `${formatPercent(card.percent)} · ${formatMoneyKopecks(card.sum)}` +
                                (card.key === "failed" ? ` · возвратов ${formatInt(report.total.refundedCount)}` : "")
                            }
                        />
                    ))}
                    <MetricHint label="Success rate" value={formatPercent(report.total.successRate)}
                                hint="успешные / (успешные + неуспешные)" />
                </Box>
            </AnalyticsCard>

            <AnalyticsCard title="По играм" subtitle="Группы статусов в разрезе игр" howToRead={HOW_TO_READ}
                           snapshotLabel="period">
                <GamesTable total={report.total} byGame={report.byGame} />
                <SeriesToggle series={groupSeries} hidden={hiddenGroups}
                              onToggle={(key) => setHiddenGroups((h) => toggleKey(h, key))} />
                <BarChart
                    height={300}
                    xAxis={[{ data: bars.labels, scaleType: "band" }]}
                    series={visibleSeries(groupSeries, hiddenGroups).map((s) => ({
                        data: s.data,
                        label: s.label,
                        color: s.color,
                        stack: "groups",
                        valueFormatter: (v: number | null) => formatInt(v ?? 0),
                    }))}
                    margin={{ left: 60, right: 20, top: 50, bottom: 40 }}
                />
            </AnalyticsCard>

            <AnalyticsCard
                title="Динамика"
                subtitle="Число ордеров и success rate по времени"
                howToRead={
                    <HowTo items={[
                        ["Точка", "интервал выбранного шага (час, день, месяц); пустые интервалы пропущены"],
                        ["Ордера", "число предметов по группам; переключатели над графиком скрывают и показывают линии"],
                        ["Success rate", "успешные / (успешные + неуспешные) в интервале; если завершённых не было — разрыв линии"],
                    ]} />
                }
                snapshotLabel="period"
            >
                <Typography level="title-sm">Ордера</Typography>
                <SeriesToggle series={ORDER_SERIES} hidden={hiddenOrders}
                              onToggle={(key) => setHiddenOrders((h) => toggleKey(h, key))} />
                <AnalyticsSeriesChart
                    dataset={dataset}
                    granularity={granularity}
                    series={visibleSeries(ORDER_SERIES, hiddenOrders)}
                />
                <Typography level="title-sm">Success rate</Typography>
                <AnalyticsSeriesChart
                    dataset={dataset}
                    granularity={granularity}
                    series={[
                        {
                            dataKey: "successRate",
                            label: "Success rate, %",
                            color: GROUPS[0].color,
                            valueFormatter: formatPercent,
                        },
                    ]}
                />
            </AnalyticsCard>
        </>
    );
}

function GamesTable({ total, byGame }: { total: OrderSummaryRow; byGame: OrderSummaryRow[] }) {
    return (
        <ScrollTable>
            <Table size="sm" stickyHeader sx={{ minWidth: 900 }}>
                <thead>
                    <tr>
                        <th scope="col">Игра</th>
                        <th scope="col">Всего</th>
                        <th scope="col">Сумма</th>
                        {GROUPS.map((g) => (
                            <th scope="col" key={g.key}>{g.label}</th>
                        ))}
                        <th scope="col">Success rate</th>
                    </tr>
                </thead>
                <tbody>
                    {byGame.map((row) => (
                        <GameRow key={row.gameId ?? row.gameName} row={row} />
                    ))}
                </tbody>
                <tfoot>
                    <GameRow row={total} isTotal />
                </tfoot>
            </Table>
        </ScrollTable>
    );
}

function GameRow({ row, isTotal = false }: { row: OrderSummaryRow; isTotal?: boolean }) {
    const weight = isTotal ? 700 : undefined;
    return (
        <tr style={{ fontWeight: weight }}>
            <td>{row.gameName}</td>
            <td>{formatInt(row.totalCount)}</td>
            <td>{formatMoneyKopecks(row.totalSum)}</td>
            {groupCards(row).map((card) => (
                <td key={card.key}>
                    {formatInt(card.count)} ({formatPercent(card.percent)})
                </td>
            ))}
            <td>{formatPercent(row.successRate)}</td>
        </tr>
    );
}
